// Legal action item 12 / lawyer-review item 7 — the refund surface has to
// name the money it is NOT offering to refund.
//
// Money in a live offer is escrowed, so it is capped out of every
// /wallet/refundable-topups row and the refund page renders an empty list.
// Offers never auto-expire, so nothing releases that hold on its own while
// the 14-day window burns down. This callout is the honest version of that
// screen: the amount, why it is unavailable, the clock, and the one action
// that frees it.
//
// Self-gating — renders nothing when the caller has no live offers, so the
// hosting page can mount it unconditionally.

import { Link } from "react-router-dom";

import { formatCents } from "../marketplace/feeMath";
import {
  REFUND_WINDOW_DAYS,
  useHeldOfferFunds,
  useOpenRefundWindows,
} from "./useHeldOfferFunds";

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

export function HeldFundsNotice() {
  const held = useHeldOfferFunds();
  // The clock is only ever rendered inside the held branch below, and its
  // history fallback can cost several requests against the 30/min wallet-read
  // budget shared with /balance. Don't spend them on a notice that returns
  // null. (`heldCents` is 0 while the offers probe is in flight, so this
  // enables itself the moment there is something to say.)
  const clock = useOpenRefundWindows({
    enabled: !held.isError && held.heldCents > 0,
  });

  // A failed offers probe reads as `heldCents === 0`, which used to make this
  // whole notice vanish — the refund path silently removed by a network blip.
  // Not knowing is not the same as nothing held; say which one it is.
  if (held.isError) {
    return (
      <section
        role="status"
        data-testid="held-funds-unknown"
        aria-label="Funds held in live offers"
        className="flex flex-col gap-2 rounded-lg border border-amber-500/60 bg-card p-4 text-card-foreground"
      >
        <p className="font-body text-sm">
          We couldn&apos;t check whether any of your money is held in live
          offers. Money you have offered can&apos;t be refunded to your card
          while the offer is live, and the {REFUND_WINDOW_DAYS}-day refund
          window keeps running.
        </p>
        <p className="font-body text-sm">
          <Link
            to="/account/marketplace/listings"
            data-testid="held-funds-withdraw-link"
            className="font-semibold underline decoration-dotted"
          >
            Check your live offers →
          </Link>
        </p>
      </section>
    );
  }

  if (held.heldCents <= 0) return null;

  const soonest = clock.windows[0];
  // The negative is a claim about money the user is entitled to. Render it
  // ONLY off an answer that provably covered the whole window; an unproven
  // absence gets silence, not "your window is closed".
  const noOpenWindow = clock.proven && clock.windows.length === 0;

  return (
    <section
      role="status"
      data-testid="held-funds-notice"
      aria-label="Funds held in live offers"
      className="flex flex-col gap-2 rounded-lg border border-amber-500/60 bg-card p-4 text-card-foreground"
    >
      <h2 className="font-heading text-base font-bold">
        {formatCents(held.heldCents)} held in {held.offerCount}{" "}
        {plural(held.offerCount, "live offer", "live offers")}
      </h2>

      <p className="font-body text-sm">
        Money you have offered leaves your spendable balance and is held, so it
        can&apos;t be refunded to your card while the offer is live. It is still
        your money. Offers never expire on their own — nothing releases this
        hold unless you withdraw the offer or the seller acts on it, and the{" "}
        {REFUND_WINDOW_DAYS}-day refund window keeps running in the meantime.
      </p>

      {soonest && (
        <p
          data-testid="held-funds-window-clock"
          data-proven={clock.proven ? "true" : "false"}
          className={`font-body text-sm font-semibold ${
            soonest.daysRemaining <= 3 ? "text-amber-700" : ""
          }`}
        >
          {clock.proven ? (
            <>
              {soonest.daysRemaining}{" "}
              {plural(soonest.daysRemaining, "day", "days")} left on your
              earliest top-up — that window closes{" "}
              {formatDate(soonest.closesAt)}.
            </>
          ) : (
            // Unproven means an older top-up may exist below what we could
            // read, and an older one closes SOONER. State the bound rather
            // than a number we can't stand behind.
            <>
              At most {soonest.daysRemaining}{" "}
              {plural(soonest.daysRemaining, "day", "days")} left on your
              earliest top-up — the soonest window we can confirm closes{" "}
              {formatDate(soonest.closesAt)}.
            </>
          )}
        </p>
      )}

      {noOpenWindow && (
        <p
          data-testid="held-funds-no-window"
          className="font-body text-sm text-muted-foreground"
        >
          None of your top-ups is still inside its {REFUND_WINDOW_DAYS}-day
          window, so releasing this money won&apos;t make it refundable to your
          card. It will return to your Greenhold balance to spend on the
          platform.
        </p>
      )}

      <p className="font-body text-sm">
        <Link
          to="/account/marketplace/listings"
          data-testid="held-funds-withdraw-link"
          className="font-semibold underline decoration-dotted"
        >
          Withdraw an offer to make this money refundable →
        </Link>
      </p>
    </section>
  );
}
